import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, Mail, KeyRound, Eye, EyeOff } from "lucide-react";
import { API, fmtSize } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Lien copié");
  } catch {
    toast.error("Copie impossible — sélectionnez le lien manuellement");
  }
}

export function PasswordInput({ testid, id, value, onChange, placeholder = "••••••••", autoComplete = "current-password", className = "rounded-sm pr-10" }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input id={id} data-testid={testid} type={show ? "text" : "password"} required value={value}
        onChange={onChange} placeholder={placeholder} autoComplete={autoComplete} className={className} />
      <button type="button" data-testid={`${testid}-toggle`} onClick={() => setShow(!show)}
        aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground">
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function ActivationModePicker({ mode, onModeChange, password, onPasswordChange, confirm, onConfirmChange, testid = "activation",
  inviteTitle = "Envoyer une invitation",
  inviteText = "Un email d'invitation sera envoyé à l'utilisateur pour créer son mot de passe.",
  manualText = "Le compte sera activé immédiatement avec le mot de passe défini." }) {
  const [show, setShow] = useState(false);
  const mismatch = password && confirm && password !== confirm;
  return (
    <div className="space-y-3" data-testid={`${testid}-picker`}>
      <Label>Mode d'activation</Label>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <button type="button" data-testid={`${testid}-mode-invite`} onClick={() => onModeChange("invite")}
          className={cn("rounded-sm border p-4 text-left transition-colors duration-200",
            mode === "invite" ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:border-muted-foreground")}>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Mail className={cn("h-4 w-4", mode === "invite" ? "text-primary" : "text-muted-foreground")} /> {inviteTitle}
          </div>
          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{inviteText}</p>
        </button>
        <button type="button" data-testid={`${testid}-mode-manual`} onClick={() => onModeChange("manual")}
          className={cn("rounded-sm border p-4 text-left transition-colors duration-200",
            mode === "manual" ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:border-muted-foreground")}>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <KeyRound className={cn("h-4 w-4", mode === "manual" ? "text-primary" : "text-muted-foreground")} /> Définir un mot de passe
          </div>
          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{manualText}</p>
        </button>
      </div>
      {mode === "manual" && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Mot de passe *</Label>
            <div className="relative">
              <Input data-testid={`${testid}-password`} type={show ? "text" : "password"} value={password}
                onChange={(e) => onPasswordChange(e.target.value)} className="rounded-sm pr-9" placeholder="8 caractères minimum" />
              <button type="button" data-testid={`${testid}-toggle-password`} onClick={() => setShow(!show)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Confirmer le mot de passe *</Label>
            <Input data-testid={`${testid}-password-confirm`} type={show ? "text" : "password"} value={confirm}
              onChange={(e) => onConfirmChange(e.target.value)} className="rounded-sm" />
          </div>
          {mismatch && <p data-testid={`${testid}-mismatch`} className="col-span-full text-xs font-medium text-primary">Les mots de passe ne correspondent pas.</p>}
        </div>
      )}
    </div>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel = "Confirmer", onConfirm, testid }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm" data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
          <Button data-testid={`${testid}-confirm`} onClick={run} disabled={busy} className="rounded-sm bg-primary text-white hover:bg-red-700">
            {busy ? "…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Thumb({ file, className = "h-16 w-16", testid }) {
  const [failed, setFailed] = useState(false);
  const url = `${API}/files/${file.id}`;
  if (failed) {
    return (
      <a href={url} target="_blank" rel="noreferrer" data-testid={testid}
        className="inline-flex items-center gap-2 rounded-sm border border-border bg-card px-2.5 py-1.5 text-xs hover:bg-accent transition-colors">
        <FileText className="h-3.5 w-3.5 text-primary" />
        <span className="max-w-40 truncate font-medium">{file.original_name}</span>
        <span className="text-muted-foreground">{fmtSize(file.size)}</span>
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={testid} title={file.original_name}>
      <img src={url} alt={file.original_name} loading="lazy" onError={() => setFailed(true)}
        className={`${className} rounded-sm border border-border object-cover hover:opacity-80 transition-opacity bg-muted`} />
    </a>
  );
}
